// The variant surfaces' rules: which sibling a card is previewing, and what a
// dimension chip says.
//
// `lib/` rather than beside the components because both are decisions the views
// only *display*, and because the guard below is the one piece of this slice
// with a rule rather than a shape — the ticket asks for it under a unit test,
// which means it cannot live inside a component.

/**
 * A card's variant pick, bound to the item it was made against.
 *
 * **The pairing is the whole mechanism**, not bookkeeping: the donor stores
 * `{ baseId, variantId }` together (`item-card.tsx:77-78`) so that a component
 * instance reused for a different item — which is what a virtualised or
 * reordered list does — can tell that its selection belongs to an item that is
 * no longer on screen.
 */
export interface CatalogVariantSelection {
  /** The item the pick was made against. */
  baseId: number;
  /** The sibling picked, or `baseId` itself when nothing has been picked. */
  variantId: number;
}

/** Nothing picked yet — the item previews itself. */
export const ownItemSelection = (itemId: number): CatalogVariantSelection => ({
  baseId: itemId,
  variantId: itemId,
});

/**
 * Which item a card should display — the stale-preview guard.
 *
 * `selection.baseId === itemId ? selection.variantId : itemId`, which is the
 * donor's line exactly. A mismatch means this instance is now rendering a
 * different item than the one the pick was made against, so the pick is
 * discarded rather than shown: a stale preview can never leak across cards or
 * rows.
 *
 * The equality is strict on the *base* id rather than a check that the variant
 * is still in the group, and that is the donor's choice: the group membership a
 * card knows is its own item's, so "is this pick mine" is the only question the
 * card can actually answer.
 */
export function resolveVariantSelection(
  selection: CatalogVariantSelection,
  itemId: number,
): number {
  return selection.baseId === itemId ? selection.variantId : itemId;
}

/** The three decimals a dimension label is built from, as the wire carries them. */
export interface CatalogVariantDimensions {
  dimensionWidth?: string | null;
  dimensionHeight?: string | null;
  dimensionDepth?: string | null;
}

/**
 * One dimension, formatted as the donor's chips format it — or `null` when the
 * item has nothing to say for it.
 *
 * `toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 2 })`
 * is the donor's call verbatim (`DimensionVariants.tsx:63-72`), so `24` prints
 * `24.0`, `24.567` prints `24.57`, and `1024` prints `1,024.0` — the grouping
 * separator included, because the locale is named rather than left to the
 * reader's.
 *
 * **A zero is absent, not `0.0`.** The donor guards each value with a bare
 * truthiness test on a *number* out of its object cache, where `0` is falsy. The
 * Atlas wire carries decimals as strings to keep them off a float, and `"0"` is
 * truthy — so parsing before the guard is what keeps this printing what modsy
 * prints. An unparseable value has no reading either and takes the same arm,
 * exactly as `formatDimension` does for the details table.
 */
function dimensionPart(value: string | null | undefined, suffix: string): string | null {
  const trimmed = value?.trim();
  if (!trimmed) return null;
  const parsed = Number(trimmed);
  if (!Number.isFinite(parsed) || parsed === 0) return null;
  const printed = parsed.toLocaleString("en-US", {
    minimumFractionDigits: 1,
    maximumFractionDigits: 2,
  });
  return `${printed}${suffix}`;
}

/**
 * The dimension string a chip is labelled with, and the one the `Size:` heading
 * above the row repeats — `null` when the item has no dimension to print.
 *
 * **Width, then depth, then height**, joined by `' x '`. That order is the
 * donor's array order and is deliberately *not* the Width/Height/Depth order the
 * Add Variant form lays its three fields out in — the two disagree in the source
 * and both are carried as they are.
 *
 * `null` rather than `""` because the caller has two things to do with it: skip
 * the chip, or hide the whole row.
 */
export function variantDimensionLabel(item: CatalogVariantDimensions): string | null {
  const parts = [
    dimensionPart(item.dimensionWidth, "W"),
    dimensionPart(item.dimensionDepth, "D"),
    dimensionPart(item.dimensionHeight, "H"),
  ].filter((part): part is string => part !== null);

  return parts.length === 0 ? null : parts.join(" x ");
}

/**
 * One member of a variant group, as the two chip rows read it.
 *
 * A structural subset, so the details page's `CatalogDetailItem[]` satisfies it
 * without a cast — the group's members arrive on the detail response as full
 * item summaries (`itemDetailResponseWire.variants`), which is why neither row
 * has to fetch anything.
 */
export interface CatalogVariantMember extends CatalogVariantDimensions {
  id: number;
  finish?: { name: string } | null;
}

/** One chip: the variant it switches to, what it reads, and whether it is the
 * one on screen. */
export interface CatalogVariantChip {
  itemId: number;
  label: string;
  isCurrent: boolean;
}

/**
 * A chip row — what the heading names, and the chips under it — or `null` when
 * the row does not draw at all.
 *
 * The two rules are separate on purpose. `current` is the *displayed* variant's
 * value and gates the row; `chips` skips members with nothing to print. An item
 * with a finish whose siblings have none still draws a one-chip row, which is
 * the donor's behavior and would be lost by a single "has anything to show"
 * test.
 */
export interface CatalogVariantChipRow {
  current: string;
  chips: CatalogVariantChip[];
}

/** The shared shape of both rows: gate on the current value, chip on each
 * member's own. */
function chipRow(
  current: CatalogVariantMember,
  variants: readonly CatalogVariantMember[],
  read: (member: CatalogVariantMember) => string | null,
): CatalogVariantChipRow | null {
  const currentLabel = read(current);
  if (currentLabel === null) return null;

  return {
    current: currentLabel,
    chips: variants.flatMap((member) => {
      const label = read(member);
      return label === null
        ? []
        : [{ itemId: member.id, label, isCurrent: member.id === current.id }];
    }),
  };
}

/** A member's finish name, or `null` — an all-whitespace name counts as none,
 * which is what the donor's `finish?.name || ''` truthiness test amounts to. */
const finishName = (member: CatalogVariantMember): string | null =>
  member.finish?.name.trim() || null;

/**
 * The `Color: {finish}` row — `null` when the displayed variant has no finish
 * name (`FinishVariants.tsx:33-35`).
 *
 * Chips keep the group's own order: the donor sorts nothing and does not move
 * the current chip to the front (`objectCache.ts:141-154`). The *Add Variant
 * dialog's* preview strip does move its current tile first, and that difference
 * between the two surfaces is real.
 */
export function finishVariantRow(
  current: CatalogVariantMember,
  variants: readonly CatalogVariantMember[],
): CatalogVariantChipRow | null {
  return chipRow(current, variants, finishName);
}

/**
 * The `Size: {dimensions}` row — `null` when the displayed variant has no
 * non-zero dimension (`DimensionVariants.tsx:28-31`).
 */
export function dimensionVariantRow(
  current: CatalogVariantMember,
  variants: readonly CatalogVariantMember[],
): CatalogVariantChipRow | null {
  return chipRow(current, variants, variantDimensionLabel);
}

/**
 * The group member the details page renders, falling back to the item itself.
 *
 * **The whole page follows this, not just the chips.** The donor selects its
 * `item` by the *variant* id rather than by the id the URL named
 * (`ItemDetailsIsland.tsx:117`), so the badges, the manufacturer line, the name,
 * the details table, the usage count, the manufacturer-site link and the
 * description all swap together when a chip is clicked. Only the URL stays put.
 *
 * No fetch is needed to do it: the detail response types `item` and each entry of
 * `variants` as the *same* `itemSummaryWire`, so a sibling already carries every
 * field the page reads off the item. That mirrors the donor, where "loading the
 * active item also loads all its variants" into the object cache and the swap is
 * a cache read rather than a request.
 *
 * The fallback is not defensive dressing: `variants` is empty for an ungrouped
 * item (the wire's own note says to read `variantGroupId`, never an empty
 * `variants`, to ask whether an item is grouped), and a group can lose a member
 * between a click and a render. Either way the page shows the item the URL asked
 * for, which is the only thing it can be sure exists.
 */
export function displayedVariant<T extends { id: number }>(
  item: T,
  variants: readonly T[],
  displayedId: number,
): T {
  return variants.find((variant) => variant.id === displayedId) ?? item;
}

/**
 * The pictures the details page's gallery draws — the *displayed variant's*, not
 * the URL item's (#413).
 *
 * **The whole group arrives with the item**, which is what makes the swap free:
 * the donor's falcon hydrates every sibling's full `images` list into one
 * `load-item` payload (`apps/items/shared/router.py:410-419`), so
 * `<Images item={item} />` reads the selected variant's set straight out of the
 * object cache (`Lightbox/Images.tsx:26`) and there is no frame in modsy where a
 * variant's thumbnail strip is missing. Atlas carries the same group on
 * `variantImages`, so this is a lookup rather than a fetch.
 *
 * The item's own set is **not** repeated in `variantImages` — it is `images`, and
 * duplicating the heaviest object on the response to save this branch would be a
 * poor trade. So: own item, `images`; sibling, its entry.
 *
 * **The last arm is degradation, and it keys on the field, not on the entry.**
 * The field is optional on the wire, so a service predating it omits it
 * wholesale; there the chip's own summary hero is all that is known, and drawing
 * it is the donor's own branch for exactly that shape (`Images.tsx:26-28` —
 * images empty, hero known, render the lone hero). One correct picture beats an
 * empty panel, and only the strip is lost.
 *
 * A **present** `variantImages` missing an entry is the opposite fact: absent
 * means "this member has no pictures", which is the contract's own convention,
 * so the answer is the empty set the gallery's placeholder arm renders. Reading
 * the summary hero there would be reading a *different query* — `heroImage`
 * comes from `heroes_by_item` and the galleries from `list_for_items`, two
 * statements under READ COMMITTED, so an image removed between them leaves a
 * hero that the gallery no longer has. Testing `undefined` rather than the
 * entry's absence is what keeps a deleted picture off the screen and the
 * ticket's empty arm on it.
 */
export function displayedImages<TImage>(
  data: {
    item: { id: number };
    images?: readonly TImage[];
    variantImages?: readonly { itemId: number; images: readonly TImage[] }[];
  } | null,
  shown: { id: number; heroImage?: TImage | null } | null,
): readonly TImage[] {
  if (data === null || shown === null) return [];
  if (shown.id === data.item.id) return data.images ?? [];
  if (data.variantImages === undefined) return shown.heroImage ? [shown.heroImage] : [];
  return data.variantImages.find((member) => member.itemId === shown.id)?.images ?? [];
}

/**
 * One detail response's group galleries, re-pointed at a *different* member of
 * the same group — what a write's cache correction needs (#413).
 *
 * Every single-item write answers with the written item's `ItemDetailOutput`,
 * and the mutations patch every cached member of the group from it so the page
 * updates on the frame the dialog closes. `variants` transfers as-is — it is the
 * same membership from anyone's point of view — but `variantImages` does not:
 * it is *siblings of the response's item*, and each cached member needs siblings
 * of **itself**.
 *
 * Two edits turn one into the other. The cached member's own entry comes **out**
 * (its pictures are its `images`, and an entry for itself would shadow them),
 * and the response item's own gallery goes **in** — carried on `images`, since
 * nothing lists a member as its own sibling.
 *
 * That second half is added only when the response's item is still **alive**,
 * which the wire says by listing it in `variants` ("the group's *alive*
 * members"). So an add-variant contributes the new sibling's pictures, and a
 * delete — whose response names a soft-deleted item — contributes nothing, and
 * the retired member simply drops out.
 *
 * **An omitted field stays omitted**, which is why this returns `undefined`
 * rather than `[]` for a response that predates it. The two are not
 * interchangeable downstream: `displayedImages` reads a present field as the
 * whole truth about the group and an absent one as "this service cannot say",
 * so writing `[]` into a cache entry would assert that every sibling has no
 * pictures and blank a gallery that has a hero to draw. Answering `undefined`
 * leaves the entry in the state the response actually justifies.
 */
export function regroupVariantImages<TImage>(
  forItemId: number,
  result: {
    item: { id: number };
    variants: readonly { id: number }[];
    images?: readonly TImage[];
    variantImages?: readonly { itemId: number; images: readonly TImage[] }[];
  },
): { itemId: number; images: readonly TImage[] }[] | undefined {
  if (result.variantImages === undefined) return undefined;
  const others = result.variantImages.filter((entry) => entry.itemId !== forItemId);
  const writtenIsAlive = result.variants.some(({ id }) => id === result.item.id);
  const own =
    writtenIsAlive && result.item.id !== forItemId && (result.images ?? []).length > 0
      ? [{ itemId: result.item.id, images: result.images ?? [] }]
      : [];
  return [...others, ...own];
}

/** One group as the list response carries it — an id and its alive members. */
export interface CatalogVariantGroup<TMember> {
  id: number;
  members: readonly TMember[];
}

/**
 * The tiles one card draws: its own group's members, or none.
 *
 * The join exists because the groups ride **beside** the results rather than on
 * each row — the wire's note is that "a copy per row would repeat one group's
 * images once for every sibling the page matched". The donor has no equivalent
 * step: falcon puts a `variants` array straight onto each item DTO.
 *
 * `variantGroupId` is the question, not `members.length` — the wire says so
 * explicitly, and it is the difference between "this item has no siblings" and
 * "this page's response did not carry the group". Both draw nothing here, but
 * only the first is a fact about the item.
 */
export function variantTilesFor<TMember>(
  item: { variantGroupId?: number | null },
  groups: readonly CatalogVariantGroup<TMember>[] | undefined,
): readonly TMember[] {
  if (item.variantGroupId == null) return [];
  return groups?.find((group) => group.id === item.variantGroupId)?.members ?? [];
}

/**
 * The Add Variant dialog's preview strip — the group with the dialog's own item
 * moved to the front, or nothing at all.
 *
 * **The third variant strip, and the only one that reorders.** The catalog
 * cards' strip draws the group in its own order and the details page's chips do
 * too; this one puts `baseItemId` first "so that the first variants tile always
 * corresponds to the original item"
 * (`AddVariantIsland/components/item-details.tsx:253-254`). The ordering key is
 * the item the dialog was *opened from*, not the one currently on show — so the
 * tiles never move as someone clicks through them; only the highlight travels.
 *
 * **Two members is the floor.** The donor draws nothing for a group of one,
 * which is the strictest of the three gates: `heroImgUrls.length < 2 ||
 * itemIds.length < 2` returns `null` (the array-length half of that guard is
 * defending against a falcon DTO where the images ride beside the ids; Atlas's
 * members carry their own, so "at least two" is all that is left of it).
 *
 * Nothing, too, when the base item is not in the group it named — the donor's
 * `currentItemIndex === -1` arm. It cannot happen from the dialog, and it is the
 * donor's own answer for it.
 */
export function previewVariantOrder<T extends { id: number }>(
  baseItemId: number,
  variants: readonly T[],
): readonly T[] {
  if (variants.length < 2) return [];
  const base = variants.find((variant) => variant.id === baseItemId);
  if (base === undefined) return [];
  return [base, ...variants.filter((variant) => variant.id !== baseItemId)];
}

/** The Item Search island card's tile cap (`VariantTiles.tsx`, `MAX_VARIANT_TILES`). */
const SEARCH_CARD_MAX_THUMBS = 4;

/**
 * The Item Search island card's strip (#798): `previewVariantOrder`'s ordering
 * and gates over the list response's group members, capped at four tiles, plus
 * how many members the cap left out — the strip's `+N variants`.
 *
 * The donor's island strip is the preview's code with a cap: it starts from the
 * card's own item and appends members in group order while fewer than four are
 * drawn, and counts `Math.max(itemIds.length - 4, 0)`
 * (`ItemSearchIsland/components/ItemCard/VariantTiles.tsx`).
 */
export function searchCardVariantThumbs<TImage>(
  ownItemId: number,
  members: readonly { itemId: number; heroImage?: TImage | null }[],
): { thumbs: { id: number; heroImage: TImage | null }[]; moreCount: number } {
  const ordered = previewVariantOrder(
    ownItemId,
    members.map((member) => ({ id: member.itemId, heroImage: member.heroImage ?? null })),
  );
  return {
    thumbs: ordered.slice(0, SEARCH_CARD_MAX_THUMBS),
    moreCount: Math.max(ordered.length - SEARCH_CARD_MAX_THUMBS, 0),
  };
}

/**
 * Whether adding a variant should switch the page onto the one just made.
 *
 * **Only when the dialog was opened from the item the URL names.** The donor's
 * observer guards the switch with `payload.itemId === activeItemId`
 * (`ItemDetailsIsland/data/observers.ts:175`), where `activeItemId` is the id the
 * page was entered with and never moves on a chip click
 * (`ItemDetailsIsland.tsx:87-93`), while the payload's id is the *displayed*
 * variant's (`Header/ActionMenu.tsx:127`). So adding a variant of a sibling you
 * clicked through to leaves modsy's page on that sibling.
 *
 * Named and exported because the two states are one comparison apart and the
 * comparison is the whole behavior — and because two hosts ask it: the item
 * page route, and the Item Search island's item dialog (#798), whose
 * dialog-mode observer is the same guard (`dialogParentId` is then the
 * displayed variant, `urlItemId` the item the dialog was opened on).
 */
export function switchesToAddedVariant(urlItemId: number, dialogParentId: number): boolean {
  return urlItemId === dialogParentId;
}
